import * as React from "react";
import { cn } from "../lib/utils";

interface InfoNoteProps {
  children: React.ReactNode;
  /**
   * Optional leading glyph (e.g. lucide `Info`). Omit for a plain prose
   * note. Rendered muted — this is supplementary context, not an alert.
   */
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}

/**
 * InfoNote — a quiet, muted callout for supplementary context embedded
 * inside a form section ("this field is hidden because…", "enabling this
 * lets…"). The understated sibling of `AlertBanner`:
 *
 *   - AlertBanner: page-level, color-tinted, iconed, `role="alert"`,
 *     dismissible — for things the user should NOTICE (warnings, results).
 *   - InfoNote: in-flow, muted, no `role="alert"` — for things the user
 *     reads in passing. Using `role="alert"` here would wrongly announce
 *     static prose to screen readers.
 *
 * Use this for every embedded "info note" callout instead of hand-rolling
 * a `bg-muted` border div (the pattern this primitive replaces).
 */
export function InfoNote({ children, icon: Icon, className }: InfoNoteProps) {
  return (
    <div
      className={cn(
        "rounded-lg border border-border/40 bg-muted/20 p-3 text-sm text-muted-foreground leading-relaxed",
        Icon && "flex items-start gap-3",
        className,
      )}
    >
      {Icon && (
        <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground/70" />
      )}
      {Icon ? <div>{children}</div> : children}
    </div>
  );
}
